import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, CheckCircle2, Wallet } from "lucide-react";

export default function SubscriptionBanner({ user }) {
  const [loading, setLoading] = useState(null);
  const [providers, setProviders] = useState({ stripe: true, mercadopago: false });
  const isAdmin = user?.role === "tenant_admin";

  useEffect(() => {
    if (isAdmin) api.get("/payments/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, [isAdmin]);

  if (!user?.tenant_id) return null;
  const status = user.subscription_status;

  if (status === "active") {
    return (
      <div className="mb-8 flex items-center gap-3 rounded-xl border border-teal-500/30 bg-teal-500/10 px-4 py-3 text-sm text-teal-200" data-testid="subscription-active-banner">
        <CheckCircle2 className="w-4 h-4 text-teal-400" /> Assinatura ativa — Plano Essencial (R$ 299/mês)
      </div>
    );
  }

  const daysLeft = user.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / 86400000)) : null;
  const expired = daysLeft === 0 || status === "canceled";

  const subscribe = async (provider) => {
    setLoading(provider);
    try {
      const url = provider === "mercadopago" ? "/payments/mercadopago/subscription" : "/payments/checkout";
      const { data } = await api.post(url, { lookup_key: "essencial_mensal", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setLoading(null);
    }
  };

  return (
    <div className={`mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-xl border px-5 py-4 ${expired ? "border-rose-500/40 bg-rose-500/10" : "border-amber-500/30 bg-amber-500/10"}`} data-testid={expired ? "subscription-expired-banner" : "subscription-trial-banner"}>
      <div className="text-sm">
        <div className="font-semibold text-white">
          {expired ? "Seu período de teste terminou — canal suspenso" : `Teste grátis — ${daysLeft} ${daysLeft === 1 ? "dia restante" : "dias restantes"}`}
        </div>
        <div className="text-slate-300">
          {expired ? "Novas denúncias e ações no painel estão bloqueadas até a assinatura do plano Essencial (R$ 299/mês)." : "Assine o plano Essencial por R$ 299/mês para manter o canal ativo."}
        </div>
      </div>
      {isAdmin && (
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button onClick={() => subscribe("stripe")} disabled={!!loading} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="subscribe-btn">
            {loading === "stripe" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />} Assinar com cartão
          </Button>
          {providers.mercadopago && (
            <Button onClick={() => subscribe("mercadopago")} disabled={!!loading} variant="outline" className="border-sky-500/50 bg-transparent text-sky-200 hover:bg-sky-500/10" data-testid="subscribe-mp-btn">
              {loading === "mercadopago" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wallet className="w-4 h-4 mr-2" />} Mercado Pago
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
